import React, { useContext, useEffect, useMemo, useState } from 'react'
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  Stack,
  Button,
  IconButton,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Tooltip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Paper,
  Alert,
  CardActionArea,
} from '@mui/material'
import {
  Search,
  Add,
  Edit,
  Visibility,
  Checklist,
  EventRepeat,
  Shield,
  ToggleOn,
  Block,
} from '@mui/icons-material'
import API from '../../../services/API'
import Loading from '../../loading/loadingPage'
import { GlobalContext } from '../../../App'
import { useNavigate } from 'react-router-dom'

const frequencyLabel = (days) => {
  if (!days) return 'Ad hoc'
  if (days === 1) return 'Daily'
  if (days === 7) return 'Weekly'
  if (days === 30) return 'Monthly'
  if (days === 90) return 'Quarterly'
  if (days === 365) return 'Annually'
  return `Every ${days} days`
}

const categoryColours = {
  IPC: 'success',
  Medicines: 'secondary',
  Safeguarding: 'warning',
  Governance: 'info',
  'Clinical Records': 'primary',
  'Fleet & Equipment': 'error',
  Workforce: 'default',
  Equipment: 'default',
  Events: 'primary',
  Operations: 'warning',
  Administration: 'default',
}

export default function AuditDescriptionsDashboard() {
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState(false)
  const [auditDescriptions, setAuditDescriptions] = useState([]) // API data here
  const [search, setSearch] = useState('')
  const [category, setCategory] = useState('All')
  const [selectedAudit, setSelectedAudit] = useState(null)
  const [openView, setOpenView] = useState(false)
  const { setToast } = useContext(GlobalContext)
  const navigate = useNavigate()

  useEffect(() => {
    API.get('/governance/audits/templates')
      .then((res) => {
        if (res.status === 200) {
          setAuditDescriptions(res.data)
          console.log(res.data)
        } else {
          setFetchError(true)
        }
      })
      .catch((e) => {
        console.error(e.message)
        setFetchError(true)
      })
      .finally(() => setLoading(false))
  }, [])

  const categories = useMemo(() => {
    const unique = [
      ...new Set(auditDescriptions.map((a) => a.category).filter(Boolean)),
    ]
    return ['All', ...unique]
  }, [auditDescriptions])

  const filteredAudits = useMemo(() => {
    return auditDescriptions.filter((audit) => {
      const matchesSearch =
        audit.title?.toLowerCase().includes(search.toLowerCase()) ||
        audit.auditDescription?.toLowerCase().includes(search.toLowerCase()) ||
        audit.category?.toLowerCase().includes(search.toLowerCase())

      const matchesCategory = category === 'All' || audit.category === category

      return matchesSearch && matchesCategory
    })
  }, [auditDescriptions, search, category])

  const stats = useMemo(() => {
    return {
      total: auditDescriptions.length,
      active: auditDescriptions.filter((a) => a.isActive).length,
      required: auditDescriptions.filter((a) => a.isRequired).length,
      system: auditDescriptions.filter((a) => a.isSystemTemplate).length,
    }
  }, [auditDescriptions])

  const handleView = (audit) => {
    setSelectedAudit(audit)
    setOpenView(true)
  }

  const handleToggleRequired = async (audit) => {
    API.patch(`/governance/audits/templates/${audit._id}`, {
      isRequired: !audit.isRequired,
    }).then((res) => {
      if (res.status === 200) {
        setToast({
          message: 'Audit Template Updated',
        })
      } else {
        setToast({
          severity: 'error',
          message: 'Unable to update Audit Template',
        })
      }
    })

    setAuditDescriptions((prev) =>
      prev.map((item) =>
        item._id === audit._id
          ? { ...item, isRequired: !item.isRequired }
          : item,
      ),
    )
  }

  const handleToggleActive = async (audit) => {
    API.patch(`/governance/audits/templates/${audit._id}`, {
      isActive: !audit.isActive,
    }).then((res) => {
      if (res.status === 200) {
        setToast({
          message: 'Audit Template Updated',
        })
      } else {
        setToast({
          severity: 'error',
          message: 'Unable to update Audit Template',
        })
      }
    })

    setAuditDescriptions((prev) =>
      prev.map((item) =>
        item._id === audit._id ? { ...item, isActive: !item.isActive } : item,
      ),
    )
  }

  if (loading) {
    return <Loading />
  }

  if (fetchError) {
    return <Alert severity="error">Unable to fetch Audit Templates</Alert>
  }
  return (
    <Box sx={{ p: 3 }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', md: 'center' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="title2" fontWeight={800}>
            Audit Templates
          </Typography>
          <Typography color="secondary.main">
            Manage audit templates, required frequencies and governance checks.
          </Typography>
        </Box>

        <Button
          variant="contained"
          startIcon={<Add />}
          sx={{ borderRadius: 3, px: 3 }}
          onClick={() => navigate('/governance/audit-templates/new')}
        >
          Create Template
        </Button>
      </Stack>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <StatCard
          icon={<Checklist />}
          label="Total Audits"
          value={stats.total}
        />
        <StatCard icon={<ToggleOn />} label="Active" value={stats.active} />
        <StatCard icon={<Shield />} label="Required" value={stats.required} />
        <StatCard
          icon={<EventRepeat />}
          label="System Templates"
          value={stats.system}
        />
      </Grid>

      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          borderRadius: 4,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            fullWidth
            placeholder="Search audits..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />

          <FormControl sx={{ minWidth: 220 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={category}
              label="Category"
              onChange={(e) => setCategory(e.target.value)}
            >
              {categories.map((cat) => (
                <MenuItem key={cat} value={cat}>
                  {cat}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      <Grid container spacing={2}>
        {filteredAudits.map((audit) => (
          <Grid item xs={12} md={6} xl={4} key={audit._id || audit.title}>
            <Card
              elevation={0}
              sx={{
                height: '100%',
                borderRadius: 4,
                border: '1px solid',
                borderColor: 'divider',
                transition: '0.2s ease',
                '&:hover': {
                  boxShadow: 4,
                },
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <CardContent sx={{ height: '100%' }}>
                <Stack spacing={2} justifyContent="space-between" height="100%">
                  <Box>
                    <Typography variant="h6" fontWeight={800}>
                      {audit.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {audit.auditDescription}
                    </Typography>
                  </Box>

                  <Stack spacing={1}>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      <Chip
                        label={audit.category || 'Uncategorised'}
                        color={categoryColours[audit.category] || 'default'}
                        size="small"
                      />

                      <Chip
                        label={frequencyLabel(audit.requiredFrequency)}
                        icon={<EventRepeat />}
                        size="small"
                        variant="outlined"
                      />

                      {audit.isSystemTemplate && (
                        <Chip
                          label="System"
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      )}

                      {!audit.isRequired && (
                        <Chip
                          label="Not required"
                          size="small"
                          color="warning"
                        />
                      )}
                    </Stack>
                    <Divider />

                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography variant="body2" color="text.secondary">
                        {audit.questions?.length || 0} questions
                      </Typography>

                      <Stack direction="row" spacing={1}>
                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              checked={!!audit.isRequired}
                              disabled={!audit.tenantcanDisable}
                              onChange={() => handleToggleRequired(audit)}
                            />
                          }
                          label="Required"
                        />

                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              checked={!!audit.isActive}
                              onChange={() => handleToggleActive(audit)}
                            />
                          }
                          label="Active"
                        />
                      </Stack>
                    </Stack>
                    <Stack
                      direction={{ xs: 'column', lg: 'row' }}
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title="View template">
                          <IconButton
                            onClick={() => handleView(audit)}
                            sx={{
                              color: 'secondary.light',

                              '&:hover': {
                                color: 'contrast.main',
                              },
                            }}
                          >
                            <Visibility />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Edit template">
                          <IconButton
                            onClick={() =>
                              navigate(
                                `/governance/audit-templates/edit/${audit._id}`,
                              )
                            }
                            sx={{
                              color: 'secondary.light',

                              '&:hover': {
                                color: 'contrast.main',
                              },
                            }}
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Submit an Audit using this Template">
                          <IconButton
                            onClick={() =>
                              navigate(
                                `/governance/audit?template=${audit._id}`,
                              )
                            }
                            sx={{
                              color: 'secondary.light',

                              '&:hover': {
                                color: 'contrast.main',
                              },
                            }}
                          >
                            <Checklist />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Stack>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}

        {filteredAudits.length === 0 && (
          <Grid item xs={12}>
            <Paper
              elevation={0}
              sx={{
                p: 5,
                borderRadius: 4,
                textAlign: 'center',
                border: '1px dashed',
                borderColor: 'divider',
              }}
            >
              <Block sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
              <Typography variant="h6" fontWeight={700}>
                No Audit Templates found
              </Typography>
              <Typography color="text.secondary">
                Try adjusting your search or create a new audit template.
              </Typography>
            </Paper>
          </Grid>
        )}
      </Grid>

      <AuditViewDialog
        open={openView}
        audit={selectedAudit}
        onClose={() => setOpenView(false)}
      />
    </Box>
  )
}

function StatCard({ icon, label, value }) {
  return (
    <Grid item xs={6} md={3}>
      <Card
        elevation={0}
        sx={{
          borderRadius: 4,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 3,
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {icon}
            </Box>

            <Box>
              <Typography variant="h5" fontWeight={800}>
                {value}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {label}
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Grid>
  )
}

function AuditViewDialog({ open, audit, onClose }) {
  if (!audit) return null

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        <Stack>
          <Typography variant="h5" fontWeight={800}>
            {audit.title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {audit.auditDescription}
          </Typography>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <Chip label={audit.category} />
          <Chip
            label={frequencyLabel(audit.requiredFrequency)}
            variant="outlined"
          />
          <Chip
            label={audit.isRequired ? 'Required' : 'Not Required'}
            color={audit.isRequired ? 'success' : 'warning'}
          />
          <Chip
            label={audit.isActive ? 'Active' : 'Inactive'}
            color={audit.isActive ? 'primary' : 'default'}
          />
        </Stack>

        <Stack spacing={1.5}>
          {audit.questions?.map((question, index) => (
            <Paper
              key={index}
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography fontWeight={700}>
                {index + 1}. {question.name}
              </Typography>

              {question.guidance && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  {question.guidance}
                </Typography>
              )}

              <Chip
                sx={{ mt: 1 }}
                size="small"
                label={
                  question.required ? 'Required question' : 'Optional question'
                }
                color={question.required ? 'primary' : 'default'}
                variant="outlined"
              />
            </Paper>
          ))}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" startIcon={<Edit />}>
          Edit
        </Button>
      </DialogActions>
    </Dialog>
  )
}
